import { createDocumentInput } from '@xdocs/schemas';

const STORAGE_KEY = 'xdocs.generate-wizard-draft';

/** A mid-wizard draft may not have a title yet — `.partial()` re-used as
 * BOTH the runtime validator (E6: one schema, reused) AND the source of
 * truth for the restored value's type, so it stays byte-for-byte aligned
 * with `exactOptionalPropertyTypes` (zod's own `?: T | undefined` shape,
 * not TS's built-in `Partial<T>` which produces a subtly different
 * implicit-optional `?: T`). */
const draftValuesSchema = createDocumentInput.partial();

export interface GenerateWizardDraft {
  stepIndex: number;
  // `@xdocs/web` never imports `zod` directly (dependency-direction rule —
  // only `@xdocs/schemas` does), so the value type is pulled off the
  // schema's own `.parse` return rather than `z.infer<...>`.
  values: ReturnType<typeof draftValuesSchema.parse>;
}

/** Looser save-side shape: React Hook Form's `watch()` return type carries
 * optional nested fields (RHF's `DefaultValues<T>` inference, not the zod
 * OUTPUT type `CreateDocumentInput` itself), which trips `exactOptional
 * PropertyTypes` against `GenerateWizardDraft`. The value is JSON-serialized
 * either way, and `loadGenerateWizardDraft` re-validates it through the real
 * schema on read — so the write side only needs a JSON-serializable shape. */
export interface SavableGenerateWizardDraft {
  stepIndex: number;
  values: unknown;
}

/**
 * `localStorage`-backed resume state for the `/generate` wizard (CODE_
 * GUIDELINES §5.10(3)/E20: "the multi-step generate/F-*step* wizard
 * persists its current step + entered values, so a reopen lands the user
 * back on the step they left, not at step 1"). Mirrors the same
 * SSR-safe/try-catch shape as `ws/browserPersistence.ts` (localStorage is
 * small + JSON-serializable here too, no blob content, so no IndexedDB
 * needed for this slice).
 *
 * Restored `values` are re-validated through the SAME `createDocumentInput`
 * schema (`.partial()` — a mid-wizard draft may not have a title yet) rather
 * than a bespoke shape check, per E6 ("one zod schema per DTO, reused").
 */
export function loadGenerateWizardDraft(maxStepIndex: number): GenerateWizardDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { stepIndex?: unknown; values?: unknown };
    const result = draftValuesSchema.safeParse(parsed.values);
    if (!result.success) return null;
    const rawStepIndex = typeof parsed.stepIndex === 'number' ? parsed.stepIndex : 0;
    const stepIndex = Math.min(Math.max(Math.trunc(rawStepIndex), 0), maxStepIndex);
    return { stepIndex, values: result.data };
  } catch {
    // Corrupt/unavailable storage must never crash the wizard — start clean.
    return null;
  }
}

export function saveGenerateWizardDraft(draft: SavableGenerateWizardDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage full/unavailable — the wizard still works in-memory for this
    // session; it just won't survive a reload.
  }
}

/** Called once the document is actually created (`document.create` +
 * `generate.start` both succeed) — a stale draft must never resurrect a
 * wizard for a generation that already started. */
export function clearGenerateWizardDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort — nothing further to do if storage is unavailable.
  }
}
